import React, { useState, useEffect, useCallback } from "react";
import {
  Key,
  Copy,
  Shield,
  Code,
  Terminal,
  RefreshCw,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Zap,
  Lock,
  Unlock,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API_BASE_URL } from "../../constants";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

const APIAccess = () => {
  const { token } = useAuth();
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const res = await fetch(`${AUTH_URL}/facilities`, { headers });
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : [];
          const ragFacilities = list.filter(
            (f) =>
              f.primary_product_code && f.primary_product_code.includes("RAG"),
          );
          setFacilities(ragFacilities);
          if (ragFacilities.length > 0 && !facilityId) {
            setFacilityId(ragFacilities[0].facility_id || "");
          }
        }
      } catch (err) {
        console.error("Failed to load facilities:", err);
      }
    };
    fetchFacilities();
  }, [token]);

  const fetchFacilityDetails = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const res = await fetch(`${AUTH_URL}/facilities`, { headers });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const fac = list.find((f) => f.facility_id === facilityId);
        setFacility(fac || null);
      }
    } catch (err) {
      console.error("Failed to load facility details:", err);
    }
    setLoading(false);
  }, [token, facilityId]);

  const fetchUsage = useCallback(async () => {
    if (!facilityId || !facility?.api_access_enabled) return;
    setUsageLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/get-facility-usage?facility_id=${encodeURIComponent(facilityId)}&channel=api`,
        { headers },
      );
      if (res.ok) {
        setUsage(await res.json());
      }
    } catch (err) {
      console.error("Failed to load usage:", err);
    }
    setUsageLoading(false);
  }, [token, facilityId, facility?.api_access_enabled]);

  useEffect(() => {
    fetchFacilityDetails();
  }, [fetchFacilityDetails]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const toggleApiAccess = async () => {
    if (!facility) return;
    setToggling(true);
    try {
      const res = await fetch(`${AUTH_URL}/facilities`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          facility_id: facilityId,
          api_access_enabled: !facility.api_access_enabled,
        }),
      });
      if (res.ok) {
        await fetchFacilityDetails();
      }
    } catch (err) {
      console.error("Failed to toggle API access:", err);
    }
    setToggling(false);
  };

  const regenerateKey = async () => {
    if (!facility) return;
    setRegenerating(true);
    try {
      const res = await fetch(`${AUTH_URL}/regenerate-api-key`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          facility_id: facilityId,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        // Store the new key temporarily for display — it won't be retrievable again
        setFacility((prev) => ({ ...prev, api_key: data.api_key }));
        setShowKey(true);
      }
    } catch (err) {
      console.error("Failed to regenerate API key:", err);
    }
    setRegenerating(false);
    setConfirmRegen(false);
  };

  const copyToClipboard = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const maskedKey = (key) => {
    if (!key) return "No key generated";
    if (showKey) return key;
    return key.slice(0, 8) + "..." + key.slice(-4);
  };

  const apiEnabled = facility?.api_access_enabled;
  const apiKey = facility?.api_key;

  const curlExample = `curl -X POST https://n8n.hyveappliedintelligence.com/webhook/api/v1/query \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey || "YOUR_API_KEY"}" \\
  -d '{"facility_id": "${facilityId || "FACILITY_ID"}", "question": "Who are the candidates?", "top_k": 5}'`;

  const exampleResponse = `{
  "answer": "Based on the knowledge base...",
  "sources": [{"title": "...", "score": 0.92}],
  "tokens_used": {"input": 450, "output": 120}
}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="w-6 h-6 text-slate-600" />
            API Access Management
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage REST API access to query your knowledge base programmatically
          </p>
        </div>
      </div>

      {/* Facility Selector */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Select Facility
        </label>
        {facilities.length === 0 ? (
          <p className="text-sm text-slate-500">
            No RAG-enabled facilities found.
          </p>
        ) : (
          <select
            value={facilityId}
            onChange={(e) => {
              setFacilityId(e.target.value);
              setFacility(null);
              setUsage(null);
              setShowKey(false);
              setConfirmRegen(false);
            }}
            className="w-full max-w-md border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            {facilities.map((f) => (
              <option key={f.facility_id} value={f.facility_id}>
                {f.display_name || f.facility_id} ({f.facility_id})
              </option>
            ))}
          </select>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
          <span className="ml-2 text-slate-500 text-sm">Loading...</span>
        </div>
      )}

      {!loading && facility && (
        <>
          {/* API Status Card */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Key className="w-5 h-5 text-slate-600" />
                API Status
              </h3>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                  apiEnabled
                    ? "bg-green-100 text-green-700"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {apiEnabled ? (
                  <>
                    <Unlock className="w-3 h-3 mr-1" />
                    Enabled
                  </>
                ) : (
                  <>
                    <Lock className="w-3 h-3 mr-1" />
                    Disabled
                  </>
                )}
              </span>
            </div>

            {/* Toggle */}
            <div className="flex items-center justify-between bg-slate-50 rounded-lg p-4 mb-6">
              <div>
                <p className="text-sm font-medium text-slate-700">API Access</p>
                <p className="text-xs text-slate-500">
                  {apiEnabled
                    ? "External applications can query this facility's knowledge base"
                    : "Enable to allow programmatic access via REST API"}
                </p>
              </div>
              <button
                onClick={toggleApiAccess}
                disabled={toggling}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  apiEnabled
                    ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                } disabled:opacity-50`}
              >
                {toggling ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : apiEnabled ? (
                  <ToggleRight className="w-4 h-4" />
                ) : (
                  <ToggleLeft className="w-4 h-4" />
                )}
                {apiEnabled ? "Disable" : "Enable"}
              </button>
            </div>

            {!apiEnabled && (
              <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-lg p-4">
                <AlertTriangle className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-600">
                  Enable API access to query this facility's knowledge base
                  programmatically. Once enabled, you'll receive an API key and
                  access to the REST endpoint.
                </p>
              </div>
            )}

            {apiEnabled && (
              <div className="space-y-4">
                {/* API Key */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                    API Key
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center bg-slate-900 text-green-400 font-mono text-sm rounded-lg px-4 py-3">
                      <Key className="w-4 h-4 mr-3 text-slate-500 flex-shrink-0" />
                      <span className="flex-1 break-all">
                        {maskedKey(apiKey)}
                      </span>
                      <button
                        onClick={() => setShowKey(!showKey)}
                        className="ml-2 text-slate-400 hover:text-slate-200 transition-colors"
                        title={showKey ? "Hide key" : "Show key"}
                      >
                        {showKey ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() =>
                          apiKey && copyToClipboard(apiKey, setCopied)
                        }
                        className="ml-2 text-slate-400 hover:text-slate-200 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  {copied && (
                    <p className="text-xs text-green-600 mt-1">Copied!</p>
                  )}
                </div>

                {/* Regenerate Key */}
                <div className="flex items-center gap-3">
                  {!confirmRegen ? (
                    <button
                      onClick={() => setConfirmRegen(true)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Regenerate Key
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <span className="text-sm text-amber-700">
                        This will invalidate the current key. Continue?
                      </span>
                      <button
                        onClick={regenerateKey}
                        disabled={regenerating}
                        className="px-3 py-1 text-sm font-medium bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                      >
                        {regenerating ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          "Confirm"
                        )}
                      </button>
                      <button
                        onClick={() => setConfirmRegen(false)}
                        className="px-3 py-1 text-sm text-slate-600 hover:text-slate-800"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {/* Rate Limits */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                    Rate Limits
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">
                        Requests per Minute
                      </p>
                      <p className="text-lg font-semibold text-slate-800">
                        {facility.rate_limit_rpm ?? "Unlimited"}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500">Daily Limit</p>
                      <p className="text-lg font-semibold text-slate-800">
                        {facility.rate_limit_daily
                          ? facility.rate_limit_daily.toLocaleString()
                          : "Unlimited"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* API Documentation Panel */}
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mb-6">
              <Code className="w-5 h-5 text-slate-600" />
              API Documentation
            </h3>

            <div className="space-y-6">
              {/* Base URL */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                  <ExternalLink className="w-4 h-4" />
                  Base URL
                </h4>
                <div className="bg-slate-900 text-slate-100 font-mono text-sm rounded-lg px-4 py-3">
                  https://n8n.hyveappliedintelligence.com/webhook/api/v1/query
                </div>
              </div>

              {/* Authentication */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                  <Shield className="w-4 h-4" />
                  Authentication
                </h4>
                <p className="text-sm text-slate-600 mb-2">
                  Include your API key in the request header:
                </p>
                <div className="bg-slate-900 text-slate-100 font-mono text-sm rounded-lg px-4 py-3">
                  <span className="text-blue-400">X-API-Key:</span>{" "}
                  <span className="text-green-400">
                    {"{"}your_api_key{"}"}
                  </span>
                </div>
              </div>

              {/* Example Request */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Terminal className="w-4 h-4" />
                    Example Request
                  </h4>
                  <button
                    onClick={() => copyToClipboard(curlExample, setCopiedCurl)}
                    className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    {copiedCurl ? (
                      <>
                        <Check className="w-3 h-3 text-green-600" />
                        <span className="text-green-600">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <pre className="bg-slate-900 text-slate-100 font-mono text-sm rounded-lg px-4 py-3 overflow-x-auto whitespace-pre-wrap">
                  {curlExample}
                </pre>
              </div>

              {/* Example Response */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                  <Code className="w-4 h-4" />
                  Example Response
                </h4>
                <pre className="bg-slate-900 text-green-400 font-mono text-sm rounded-lg px-4 py-3 overflow-x-auto whitespace-pre-wrap">
                  {exampleResponse}
                </pre>
              </div>

              {/* Rate Limits Documentation */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4" />
                  Rate Limits
                </h4>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 space-y-2">
                  <p>
                    <span className="font-medium text-slate-700">
                      RPM (Requests per Minute):
                    </span>{" "}
                    Maximum number of API calls allowed per minute. Exceeding
                    this limit returns a{" "}
                    <code className="bg-slate-200 px-1 rounded text-xs">
                      429 Too Many Requests
                    </code>{" "}
                    response.
                  </p>
                  <p>
                    <span className="font-medium text-slate-700">
                      Daily Limit:
                    </span>{" "}
                    Maximum total API calls allowed per calendar day (UTC).
                    Resets at midnight UTC. When exceeded, requests are rejected
                    until the next day.
                  </p>
                  <p>
                    Rate limit headers are included in every response:{" "}
                    <code className="bg-slate-200 px-1 rounded text-xs">
                      X-RateLimit-Remaining
                    </code>{" "}
                    and{" "}
                    <code className="bg-slate-200 px-1 rounded text-xs">
                      X-RateLimit-Reset
                    </code>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Usage Stats */}
          {apiEnabled && (
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-slate-600" />
                  Usage Statistics
                </h3>
                <button
                  onClick={fetchUsage}
                  disabled={usageLoading}
                  className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${usageLoading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button>
              </div>

              {usageLoading && !usage && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
                  <span className="ml-2 text-slate-500 text-sm">
                    Loading usage data...
                  </span>
                </div>
              )}

              {!usageLoading && !usage && (
                <p className="text-sm text-slate-500 text-center py-8">
                  No usage data available yet.
                </p>
              )}

              {usage && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">
                      API Queries This Month
                    </p>
                    <p className="text-2xl font-bold text-slate-800 mt-1">
                      {(usage.queries_this_month ?? 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">
                      Tokens Consumed
                    </p>
                    <p className="text-2xl font-bold text-slate-800 mt-1">
                      {(usage.tokens_consumed ?? 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">
                      Last API Call
                    </p>
                    <p className="text-sm font-medium text-slate-800 mt-2">
                      {usage.last_api_call
                        ? new Date(usage.last_api_call).toLocaleString()
                        : "Never"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!loading && !facility && facilityId && (
        <div className="flex items-center justify-center py-12">
          <p className="text-sm text-slate-500">
            Unable to load facility details. Please try again.
          </p>
        </div>
      )}
    </div>
  );
};

export default APIAccess;
